/**
 * Smooth result-list updates.
 *
 * The result panel used to be rebuilt with a raw `innerHTML =` assignment on
 * every map move, which blanked the list, dropped scroll position and made
 * map-driven interactions (e.g. hover-to-pan) impossible. These helpers update
 * the list in place instead:
 *
 *  - setListContent: writes non-list content (summary / empty / grouped HTML)
 *    but skips the DOM write entirely when the markup is byte-identical to the
 *    previous render — the common case when a small pan leaves results unchanged.
 *  - renderKeyedStack: reconciles a flat stack of result cards by a stable key,
 *    reusing untouched card nodes so scroll position and the hovered card survive.
 */

const STACK_SELECTOR = ':scope > .nm-list-stack[data-keyed-stack]';

function createCardNode(html, key) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const node = tmp.firstElementChild;
    if (node) {
        node.setAttribute('data-card-key', key);
        node.__cardHtml = html;
    }
    return node;
}

/**
 * Write arbitrary list content, skipping the write when nothing changed.
 */
export function setListContent(containerEl, html) {
    if (!containerEl) {
        return;
    }
    const hasKeyedStack = !!containerEl.querySelector(STACK_SELECTOR);
    if (!hasKeyedStack && containerEl.__listContentHtml === html) {
        return;
    }
    containerEl.innerHTML = html;
    containerEl.__listContentHtml = html;
}

/**
 * Reconcile a flat stack of cards. `entries` is an ordered array of
 * `{ key, html }`; cards whose html is unchanged keep their DOM node (and thus
 * scroll position / focus), changed cards are replaced, missing keys removed.
 */
export function renderKeyedStack(containerEl, entries) {
    if (!containerEl) {
        return;
    }
    containerEl.__listContentHtml = null;

    let stack = containerEl.querySelector(STACK_SELECTOR);
    if (!stack) {
        containerEl.innerHTML = '';
        stack = document.createElement('div');
        stack.className = 'nm-list-stack';
        stack.setAttribute('data-keyed-stack', '');
        containerEl.appendChild(stack);
    }

    const existing = new Map();
    Array.prototype.forEach.call(stack.children, function(node) {
        const key = node.getAttribute('data-card-key');
        if (key != null) {
            existing.set(key, node);
        }
    });

    const desired = new Set();
    let cursor = stack.firstElementChild;

    entries.forEach(function(entry) {
        const key = String(entry.key);
        desired.add(key);

        let node = existing.get(key);
        if (node && node.__cardHtml !== entry.html) {
            // Card content changed — drop the stale node. Advance the cursor
            // first if it pointed at this node, so the insert below keeps a
            // valid (still-attached) reference.
            if (cursor === node) {
                cursor = node.nextElementSibling;
            }
            if (node.parentNode) {
                node.parentNode.removeChild(node);
            }
            existing.delete(key);
            node = null;
        }
        if (!node) {
            node = createCardNode(entry.html, key);
            if (!node) {
                return;
            }
        }

        if (cursor === node) {
            cursor = cursor.nextElementSibling;
        } else {
            stack.insertBefore(node, cursor);
        }
    });

    existing.forEach(function(node, key) {
        if (!desired.has(key) && node.parentNode === stack) {
            stack.removeChild(node);
        }
    });
}
